/**
 * Smarty Street Utility Functions
 *
 * Extracted from smarty.ts to allow proper unit testing and to keep types
 * and pure helpers importable from client code without pulling in the
 * server-only API layer.
 */

// =============================================================================
// Smarty API Response Types
// =============================================================================

export interface SmartyZipCodeResponse {
	status?: string;
	city_states?: Array<{
		city: string;
		state_abbreviation: string;
	}>;
	zipcodes?: Array<{
		alternate_counties?: Array<{
			city: string;
			state_abbreviation: string;
		}>;
	}>;
}

export interface SmartyAutocompleteSuggestion {
	street_line?: string;
	secondary?: string;
	city?: string;
	state?: string;
	zipcode?: string;
	entries?: number;
}

export interface SmartyAutocompleteResponse {
	suggestions?: SmartyAutocompleteSuggestion[];
}

export interface ZipCodeLookupResult {
	city: string;
	state: string;
	zipCode: string;
	zipCodeValid: boolean | undefined;
	cities?: Array<{ city: string; stateAbbreviation: string }>;
	error?: string;
}

export interface AddressSuggestion {
	streetLine: string;
	secondary: string;
	city: string;
	state: string;
	zipcode: string;
	entries: number;
}

export interface AutocompleteResult {
	suggestions: AddressSuggestion[];
	error?: string;
}

export interface ValuesType {
	oneLineAddress: string;
	streetAddress: string;
	city: string;
	state: string;
	zipCode: string;
	zipCodeValid?: boolean;
	streetLine?: string;
	zipcode?: string;
	secondary?: never;
	entries?: never;
}

// =============================================================================
// Input Validation
// =============================================================================

const ZIP_CODE_REGEX = /^\d{5}$/;
export const MAX_SEARCH_LENGTH = 200;
export const MAX_RESULTS_LIMIT = 20;

/**
 * Validates a US zip code format (5 digits)
 */
export function isValidZipCode(zipCode: string): boolean {
	return ZIP_CODE_REGEX.test(zipCode);
}

/**
 * Sanitizes search input to prevent injection and limit length
 */
export function sanitizeSearchInput(input: string): string {
	// Trim whitespace and limit length
	const trimmed = input.trim().slice(0, MAX_SEARCH_LENGTH);
	// Remove ASCII control characters (codes 0-31 and 127) which are invisible,
	// non-printable characters like NULL, escape sequences, backspace, etc.
	// These could be used maliciously to manipulate terminals, truncate strings,
	// or cause unexpected behavior in downstream systems.
	// biome-ignore lint/suspicious/noControlCharactersInRegex: Intentionally matching control chars for sanitization
	return trimmed.replace(/[\x00-\x1F\x7F]/g, "");
}

// =============================================================================
// Pure Address Helpers (safe to run anywhere)
// =============================================================================

/**
 * Format an address suggestion for display
 */
export function formatAutocompleteSuggestion(
	suggestion: AddressSuggestion | ValuesType,
	multipleEntries = false,
): string {
	const street = suggestion.streetLine ? `${suggestion.streetLine}` : "";
	const secondary = suggestion?.secondary ? ` ${suggestion.secondary}` : "";
	const entries = suggestion?.entries !== 0 ? ` (${suggestion.entries})` : "";
	const city = suggestion?.city ? `${suggestion.city}, ` : "";
	const state = suggestion?.state ? `${suggestion.state} ` : "";
	const zip = suggestion?.zipcode ? `${suggestion.zipcode}` : "";

	if (multipleEntries) {
		return `${street + secondary + entries}, ${city}${state}${zip}`;
	} else {
		return `${street + secondary}, ${city}${state}${zip}`;
	}
}

/**
 * Check if form values match the user's input
 */
export function checkValidityOfFormValuesAgainstInput(
	formValuesInstance: ValuesType,
): boolean {
	if (!formValuesInstance.streetAddress && formValuesInstance.oneLineAddress) {
		return false;
	}

	const mutatedFormValuesInstance = {
		...formValuesInstance,
		streetLine: formValuesInstance.streetAddress,
		zipcode: formValuesInstance.zipCode,
	};
	const formattedAddress = formatAutocompleteSuggestion(
		mutatedFormValuesInstance,
	);

	if (formattedAddress === formValuesInstance.oneLineAddress) {
		return true;
	}
	return false;
}
